import React, { useCallback, useContext, useMemo, useState } from 'react'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import Heading from 'components/Luxkit/Typography/Heading'
import LePackageUpgradeCardRoomDetails from './LePackageUpgradeCardRoomDetails'
import Group from 'components/utils/Group'
import BodyText from 'components/Luxkit/Typography/BodyText'
import InclusionsV2Section from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage/ResponsiveImage'
import Divider from 'components/Luxkit/Divider'
import { pluralizeToString } from 'lib/string/pluralize'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import SlideDown from 'components/Common/SlideDown'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import LePackageUpgradePrice from './LePackageUpgradePrice'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import LePackageUpgradeWithVelocityPoints from './LePackageUpgradeWithVelocityPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { isActiveLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { isFlashHotel } from 'lib/offer/offerTypes'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import * as Analytics from 'analytics/analytics'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import { rem } from 'polished'
import Breakpoint from 'components/Common/Breakpoint'

const StyledSlideDown = styled(SlideDown)`
  &.closed {
    margin-top: 0;
  }
`

const imageProps: ImageParams = {
  mobileAspectRatio: '11:5',
  tabletAspectRatio: '22:15',
  tabletWidth: '420px',
  quality: 'good',
}

const imagePropsWhenVelocityPointsIsPresent: ImageParams = {
  ...imageProps,
  tabletWidth: rem(150),
}

export interface PackageOptionUpgradeView {
  offer: App.Offer;
  hasFlightPrices?: boolean;
  package: App.Package;
  capacityValid?: boolean;
  isAvailable?: boolean;
  price: number;
  memberPrice: number;
  priceDifference: number;
  memberPriceDifference: number;
  isSelected?: boolean;
  withFlights?: boolean;
  value?: number;
  flightPrice?: number;
  flightsAvailable?: boolean;
}

interface Props {
  offer: App.Offer;
  upgradePackageView: OfferPackageOptionView;
  basePackageView: OfferPackageOptionView;
  hasFlights?: boolean;
}

function PackageUpgradeModal(props: Props) {
  const { offer, upgradePackageView, basePackageView, hasFlights } = props
  const modalContext = useModalElementContext<OfferPackageOptionView>()
  const isMobile = useIsMobileScreen()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const packageUpgrade = useMemo<PackageOptionUpgradeView>(() => ({
    offer,
    // no need to account for flight prices, they are always the same for the same day
    priceDifference: upgradePackageView.hotelPrice - basePackageView.hotelPrice,
    memberPriceDifference: upgradePackageView.hotelMemberPrice - basePackageView.hotelMemberPrice,
    price: upgradePackageView.hotelPrice,
    memberPrice: upgradePackageView.hotelMemberPrice,
    package: upgradePackageView.package,
    value: upgradePackageView.hotelValue,
    flightPrice: upgradePackageView.flightPrice,
    flightsAvailable: upgradePackageView.flightsAvailable,
  }), [basePackageView, upgradePackageView, offer])

  const onUpgradeClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_ACCEPTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'select_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    if (isFlashHotel(offer) && isMobile) {
      Analytics.trackClientEvent({
        subject: 'upgrade_yes',
        action: 'click',
        category: 'offer_funnel',
        type: 'interaction',
      })
    }
    modalContext.resolve(upgradePackageView)
  }, [isMobile, modalContext, offer, trackEvent, upgradePackageView])

  const onNoThanksClick = useCallback(() => {
    trackEvent('UPGRADE_MODAL_REJECTED')
    Analytics.trackClientEvent({
      subject: 'upgrade_modal',
      action: 'no_upgrade',
      category: 'logging',
      type: 'interaction',
    })
    if (isFlashHotel(offer) && isMobile) {
      Analytics.trackClientEvent({
        subject: 'upgrade_no',
        action: 'click',
        category: 'offer_funnel',
        type: 'interaction',
      })
    }
    modalContext.resolve(basePackageView)
  }, [basePackageView, isMobile, modalContext, offer, trackEvent])

  const partnership = useAppSelector((state: App.State) => state.geo.partnership?.code)
  const isLuxplusMember = useAppSelector((state: App.State) => isActiveLuxPlusMember(state))

  const isVelocityPartnership = partnership === VIRGIN_VELOCITY_PARTNERSHIP_CODE

  const name = packageUpgrade.package.name
  const roomType = packageUpgrade.package.roomType
  const firstRoomImageId = packageUpgrade.package.roomType?.images?.[0]?.id

  const { inclusions, bonusInclusions, loyaltyInclusions } = useOfferInclusions(packageUpgrade.package)

  const hasInclusionsV1 = packageUpgrade.package.inclusions && packageUpgrade.package.inclusions.length > 0
  const showInclusionV1 = hasInclusionsV1 && (inclusions.length > 0 || bonusInclusions.length > 0)
  const hasLoyaltyInclusions = loyaltyInclusions.length > 0

  const [showDetails, setShowDetails] = useState(false)
  const onViewDetails = useCallback(() => {
    setShowDetails(!showDetails)
  }, [showDetails])

  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)

  return (
    <ModalBase size="L" dismissible={false}>
      <ModalHeader
        dismissible={false}
        title={`Upgrade your room for only ${formatCurrency(packageUpgrade.priceDifference, currency, currentRegionCode, { signDisplay: 'always' })} extra`}
      />
      <ModalBody>
        <Breakpoint max="mobile">
          <div>
            {!!roomType?.images.length && <ImageGallery
              images={roomType?.images}
              imageParams={imageProps}
            />}
          </div>
        </Breakpoint>
        <ModalContent>
          <Group direction="vertical" gap={12}>
            <VerticalSpacer gap={12}>
              <CSSBreakpoint min="tablet">
                <VerticalSpacer gap={12}>
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
                    <VerticalSpacer gap={12}>
                      <Heading variant="heading4">{name}</Heading>
                      {roomType?.sizeSqm && <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>}
                      {isVelocityPartnership && <LePackageUpgradeWithVelocityPoints packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights}/>}
                      {!isVelocityPartnership && <LePackageUpgradePrice packageUpgrade={packageUpgrade}hasFlightsSelected={hasFlights} />}
                    </VerticalSpacer>
                    {firstRoomImageId && <ResponsiveImage
                      {...(isVelocityPartnership ? imagePropsWhenVelocityPointsIsPresent : imageProps)}
                      id={firstRoomImageId}
                      alt={`${roomType?.name} image 1 at ${offer.property?.name} by ${offer.property?.geoData.administrativeAreaLevel2}, ${offer.property?.geoData.administrativeAreaLevel1}, ${offer.property?.geoData.country}`}
                    />}
                  </Group>
                </VerticalSpacer>
              </CSSBreakpoint>
              <CSSBreakpoint max="mobile">
                <VerticalSpacer gap={12}>
                  <Heading variant="heading4">{name}</Heading>
                  {isVelocityPartnership && <LePackageUpgradeWithVelocityPoints packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights}/>}
                  {!isVelocityPartnership && <LePackageUpgradePrice packageUpgrade={packageUpgrade} hasFlightsSelected={hasFlights} />}
                  {roomType?.sizeSqm && <BodyText variant="small"><b>Room size:</b> {roomType.sizeSqm}m&sup2;</BodyText>}
                </VerticalSpacer>
              </CSSBreakpoint>
            </VerticalSpacer>

            <CSSBreakpoint min="tablet">
              <Divider kind="primary" />
            </CSSBreakpoint>

            <VerticalSpacer gap={16}>
              {isLuxplusMember && hasLoyaltyInclusions && <LuxPlusInclusions loyaltyInclusions={loyaltyInclusions} accommodationOffer={offer}/>}
              {showInclusionV1 && (
                <Group direction="vertical" gap={12}>
                  {(inclusions.length > 0 && bonusInclusions.length === 0) && <BodyText variant="small" weight="bold">Inclusions:</BodyText>}
                  <InclusionsSection
                    inclusions={inclusions}
                    bonusInclusions={bonusInclusions}
                    size="small"
                  />
                </Group>
              )}
              {!hasInclusionsV1 && packageUpgrade.package.inclusionsV2 && (
                <Group direction="vertical" gap={12}>
                  <BodyText variant="small" weight="bold">{pluralizeToString('inclusion', packageUpgrade.package.inclusionsV2.length)}:</BodyText>
                  <InclusionsV2Section inclusions={packageUpgrade.package.inclusionsV2} size="small" />
                </Group>
              )}
              {roomType && <StyledSlideDown show={showDetails} animateOpacity className={showDetails ? '' : 'closed'}>
                <LePackageUpgradeCardRoomDetails roomType={roomType} />
              </StyledSlideDown>}
              <SmallToggleButton size="caption" on={showDetails} onClick={onViewDetails}>
                {showDetails ? 'Hide' : 'View'} more details
              </SmallToggleButton>
              {!isLuxplusMember && hasLoyaltyInclusions && <LuxPlusInclusions loyaltyInclusions={loyaltyInclusions} accommodationOffer={offer}/>}
            </VerticalSpacer>
          </Group>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          onClick: onUpgradeClick,
          children: 'Upgrade',
        }}
        secondaryActionProps={{
          onClick: onNoThanksClick,
          children: 'No thanks',
        }}
      />
    </ModalBase>
  )
}

export default PackageUpgradeModal
